/**
 * Generic step-card framework for the onboarding wizard.
 *
 * Every onboarding step renders inside one of these: title + status badge,
 * an optional primary action, a result/children slot, error display, and a
 * collapsible manual-fallback section. Slices supply the step-specific
 * content (e.g. StripeConnectCard) through props/children; steps whose
 * slice hasn't shipped yet render the stub state.
 */
import type { ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import type { OnboardingTask } from '@limo/database'
import { getStepDefinition } from '@/lib/onboarding/steps'
import { StepStatusBadge } from './StepStatusBadge'
import { StepHelpSheet } from './StepHelpSheet'

interface StepCardProps {
  task: OnboardingTask
  /** Step-specific body (action buttons, results). Omit for stub cards. */
  children?: ReactNode
  /** Markdown-free manual instructions shown in a collapsible. */
  manualFallback?: ReactNode
}

export function StepCard({ task, children, manualFallback }: StepCardProps) {
  const definition = getStepDefinition(task.step)
  const isStub = !definition?.implemented

  return (
    <Card id={`step-${task.step}`}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="text-base">
            {definition?.label ?? task.step}
          </CardTitle>
          {definition?.description && (
            <CardDescription className="mt-1">
              {definition.description}
            </CardDescription>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <StepStatusBadge status={task.status} />
          <StepHelpSheet task={task} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isStub ? (
          <p className="text-sm text-muted-foreground italic">
            Coming soon — this step ships in a later slice.
          </p>
        ) : (
          children
        )}

        {task.error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <span className="font-medium">Error:</span> {task.error}
          </div>
        )}

        {!isStub && manualFallback && (
          <Collapsible>
            <CollapsibleTrigger className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
              <ChevronDown className="h-4 w-4" />
              Manual fallback
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
              {manualFallback}
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  )
}
